import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ABOUT_SECTIONS = [
  {
    title: 'Interviews that become action plans',
    body:
      'Tell us what you are navigating—immigration, estate, new market launch—and we capture the nuance: deadlines, helpers, missing paperwork, risk appetite. Each answer feeds an adaptive playbook so the tasks, documents, and reminders feel bespoke.',
  },
  {
    title: 'Document intelligence and verification',
    body:
      'Upload scans, photos, or PDFs and Pathfinder flags what is missing, when to notarize, and who needs the next signature. We keep the paper trail version-controlled and make it easy to prove what was filed when.',
  },
  {
    title: 'Guidance that matches your tone',
    body:
      'Choose Supportive Guide, Expert Strategist, or Focused Coach. Every nudge, summary, and weekly review adopts that voice so you stay motivated without being overwhelmed.',
  },
  {
    title: 'Premium template packs',
    body:
      'Visa Pack, Business Pack, and Inheritance Pack layer in vetted checklists, escalation paths, and document QA from specialists. They are optional upgrades for teams who want concierge-grade rigor.',
  },
  {
    title: 'Built on a transparent stack',
    body:
      'Pathfinder AI runs on a secure MERN foundation (Node/Express, MongoDB, React) with background workers managing reminders and analytics. We publish our maintenance, monitoring, and support playbooks so customers always know how the system is cared for.',
  },
];

const TERMS_SECTIONS = [
  {
    title: '1. Eligibility & Account Responsibilities',
    body:
      'You must be at least 18 years old and able to form a binding contract to use Pathfinder AI. Workspace owners are responsible for inviting collaborators, assigning appropriate roles, and keeping contact information up to date. You agree to maintain the confidentiality of your credentials and to notify us within 24 hours of any suspected compromise.',
  },
  {
    title: '2. Acceptable Use',
    body:
      'You may not upload unlawful content, probe or scan our infrastructure, interfere with other customers, or access the service via automated agents not covered by our published API rate limits. Regulated data must not be stored in Pathfinder AI unless you have a signed addendum authorizing that use. We reserve the right to suspend access that violates these rules.',
  },
  {
    title: '3. Service Commitments & Support',
    body:
      'Free plans are delivered on a best-effort basis. Paid subscriptions include a 99.5% monthly uptime target, incident notifications by email, and access to our support desk within one business day. Planned maintenance is announced at least 48 hours in advance and is excluded from uptime calculations.',
  },
  {
    title: '4. Security, Privacy & Subprocessors',
    body:
      'Passwords are hashed with bcrypt, traffic is encrypted via TLS 1.2+, and data is stored in MongoDB Atlas with encryption at rest. Operational logs are retained for 30 days and subprocessors are limited to vetted hosting, monitoring, analytics, and billing partners acting on our instructions.',
  },
  {
    title: '5. Data Ownership & Portability',
    body:
      'You retain ownership of all content you upload. You can export todos, attachments, and audit logs through the product or by contacting support. We keep rolling backups for 30 days; afterwards data is removed from active systems and backups unless retention is required by law.',
  },
  {
    title: '6. Billing, Renewals & Taxes',
    body:
      'Paid tiers renew automatically each month unless canceled. Fees are non-refundable except where required by law and you are responsible for applicable taxes. Failure to pay may result in suspension after at least seven days notice.',
  },
  {
    title: '7. Termination',
    body:
      'You may cancel at any time via workspace settings. We may suspend or terminate access if you breach the Terms, fail to pay, or if continued service would violate the law. After termination you may request a final export within 30 days before remaining data is deleted.',
  },
  {
    title: '8. Feedback & Communications',
    body:
      'Suggestions or ideas submitted to Pathfinder AI may be used without obligation or compensation. We may contact you for onboarding, updates, security notices, or legal disclosures. You can opt out of marketing messages, but administrative communications are part of the service.',
  },
  {
    title: '9. Disclaimers & Liability',
    body:
      'Pathfinder AI is provided “as is” and we disclaim all implied warranties. Our aggregate liability will not exceed the fees you paid in the preceding three months except where prohibited by law.',
  },
  {
    title: '10. Governing Law & Disputes',
    body:
      'These Terms are governed by the laws of the State of California, USA. Disputes will be resolved in the state or federal courts located in San Francisco County, and you consent to that venue.',
  },
  {
    title: '11. Contact',
    body:
      'Questions about these Terms or requests for signed agreements can be sent to legal@pathfinderhq.com. We respond to legal inquiries within five business days.',
  },
];

export default function About() {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    const targetId = hash || 'about';
    const target = document.getElementById(targetId);
    if (!target) return undefined;
    const frame = requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.hash]);

  return (
    <section className="page-shell info-page">
      <header className="page-hero" id="about">
        <p className="eyebrow">About Pathfinder AI</p>
        <h1>Concierge workflow intelligence for real life.</h1>
        <p>
          Pathfinder AI pairs consultant-style intake interviews with generated playbooks so stressful,
          bureaucratic journeys never feel like a solo mission. We translate every messy inbox thread,
          appointment, and document request into a guided sequence you can actually follow.
        </p>
      </header>

      <nav className="info-nav">
        <a href="#about-overview">About</a>
        <a href="#terms">Terms &amp; commitments</a>
      </nav>

      <article id="about-overview" className="body-copy info-section">
        {ABOUT_SECTIONS.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            <p>{section.body}</p>
          </section>
        ))}
      </article>

      <article id="terms" className="terms info-section">
        <header className="terms-hero">
          <p className="eyebrow">Legal</p>
          <h2>Terms &amp; Service Commitments</h2>
          <p>
            We built Pathfinder AI for teams who care about trust and clarity. Here is the concise version of the
            rules, followed by the details.
          </p>
        </header>
        {TERMS_SECTIONS.map((section) => (
          <section key={section.title}>
            <h3>{section.title}</h3>
            <p>{section.body}</p>
          </section>
        ))}
      </article>
    </section>
  );
}
